import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { apiClient } from '@services/api'

type Col = { key: string; label: string; num?: boolean; kind?: 'inr' | 'pct' | 'date' | 'int' | 'text' | 'badge' | 'link' }
interface Section { title: string; columns: Col[]; rows: any[] }
interface Report { id: string; title: string; subtitle: string; from: string; to: string; columns: Col[]; rows: any[]; summary: Record<string, any>; notes?: string[]; sections?: Section[] }

const iso = (d: Date) => d.toISOString().split('T')[0]
const today = () => iso(new Date())
const monthStart = (d = new Date()) => iso(new Date(d.getFullYear(), d.getMonth(), 1))
const formatINR = (n: number) => `${n < 0 ? '−' : ''}₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`
const formatDate = (s: string) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—')
const badgeClass = (v: string) => {
  const s = String(v)
  if (['Paid', 'OK', 'Yes', 'A', 'Cash', 'UPI', 'Within tolerance'].includes(s)) return 'badge-full'
  if (['Partial', 'Credit', 'B', 'Sent'].includes(s)) return 'badge-partial'
  if (['Unpaid', 'Low', 'C', 'Flagged'].includes(s)) return 'badge-unpaid'
  return 'badge-draft'
}
const cell = (col: Col, row: any) => {
  const v = row[col.key]
  if (v === null || v === undefined || v === '') return '—'
  switch (col.kind) {
    case 'inr': return formatINR(Number(v))
    case 'pct': return `${v}%`
    case 'date': return formatDate(String(v))
    case 'int': return Number(v).toLocaleString('en-IN')
    case 'badge': return <span className={`badge ${badgeClass(v)}`}>{String(v)}</span>
    case 'link': return row._link ? <Link to={row._link} className="font-semibold text-primary-500 hover:underline">{String(v)}</Link> : String(v)
    default: return typeof v === 'number' ? v.toLocaleString('en-IN') : String(v)
  }
}
const summaryLabel = (k: string) => k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).replace('Mtd', 'MTD').replace('Gst', 'GST').replace('Pct', '%')
const summaryValue = (k: string, v: any) => (typeof v === 'number' ? (/pct/i.test(k) ? `${v}%` : /kg/i.test(k) ? `${v} kg` : /^(stores|suppliers|invoices|bills|days|people|returns|restocked|flagged|sessions|packets|skus|count|stores covered|categories|products)$/i.test(k) || /^skus/i.test(k) ? v.toLocaleString('en-IN') : formatINR(v)) : String(v))

const PRESETS: Array<{ label: string; range: () => { from: string; to: string } }> = [
  { label: 'This month', range: () => ({ from: monthStart(), to: today() }) },
  { label: 'Last month', range: () => { const d = new Date(); const s = new Date(d.getFullYear(), d.getMonth() - 1, 1); const e = new Date(d.getFullYear(), d.getMonth(), 0); return { from: iso(s), to: iso(e) } } },
  { label: 'Last 30 days', range: () => { const d = new Date(); d.setDate(d.getDate() - 29); return { from: iso(d), to: today() } } },
  { label: 'This quarter', range: () => { const d = new Date(); const q = Math.floor(d.getMonth() / 3) * 3; return { from: iso(new Date(d.getFullYear(), q, 1)), to: today() } } },
  { label: 'This year', range: () => ({ from: iso(new Date(new Date().getFullYear(), 0, 1)), to: today() }) },
]

function Table({ columns, rows }: { columns: Col[]; rows: any[] }) {
  return (
    <table>
      <thead><tr>{columns.map((c) => <th key={c.key} className={c.num ? 'num' : ''}>{c.label}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={columns.length} className="text-ink-soft">No data for this period.</td></tr>}
        {rows.map((r, i) => <tr key={i}>{columns.map((c) => <td key={c.key} className={c.num ? 'num' : ''}>{cell(c, r)}</td>)}</tr>)}
      </tbody>
    </table>
  )
}

export function ReportDetail() {
  const { reportId } = useParams<{ reportId: string }>()
  const [from, setFrom] = useState(monthStart())
  const [to, setTo] = useState(today())
  const { data, isLoading, error } = useQuery({ queryKey: ['report', reportId, from, to], queryFn: () => apiClient.get<any>(`/reports/${reportId}?from=${from}&to=${to}`), enabled: !!reportId })
  const r: Report | undefined = data?.data
  const blocks: Section[] = r ? [...(r.columns?.length ? [{ title: '', columns: r.columns, rows: r.rows }] : []), ...(r.sections || [])] : []
  const activePreset = PRESETS.find((p) => { const x = p.range(); return x.from === from && x.to === to })?.label

  const exportCsv = () => {
    if (!r) return
    const esc = (v: any) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }
    const lines: string[] = [`${r.title} (${from} to ${to})`]
    for (const b of blocks) lines.push('', b.title || r.title, b.columns.map((c) => esc(c.label)).join(','), ...b.rows.map((row) => b.columns.map((c) => esc(row[c.key])).join(',')))
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = `${r.id}-${from}_to_${to}.csv`; document.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 3000)
  }

  return (
    <div>
      <div className="hdr print:hidden" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>{r?.title || 'Report'}</h1>
          <p><Link to="/dashboard/reports" className="text-primary-500 hover:underline">Reports</Link> › {r?.subtitle || ''}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-ghost" onClick={exportCsv} disabled={!r}>Export CSV</button>
          <button type="button" className="btn-ghost" onClick={() => window.print()}>Print</button>
        </div>
      </div>

      <div className="toolbar print:hidden">
        <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} title="From" />
        <input type="date" value={to} min={from} max={today()} onChange={(e) => setTo(e.target.value)} title="To" />
        {PRESETS.map((p) => <button key={p.label} type="button" className={`chip ${activePreset === p.label ? 'on' : ''}`} onClick={() => { const x = p.range(); setFrom(x.from); setTo(x.to) }}>{p.label}</button>)}
      </div>

      {error && <div className="alert alert-error">Could not load this report.</div>}
      {isLoading && <div className="card text-ink-soft">Loading…</div>}

      {r && (
        <>
          {Object.keys(r.summary || {}).length > 0 && (
            <div className="kpis" style={{ gridTemplateColumns: `repeat(${Math.min(4, Object.keys(r.summary).length)}, 1fr)` }}>
              {Object.entries(r.summary).slice(0, 8).map(([k, v]) => (
                <div key={k} className="kpi"><div className="label">{summaryLabel(k)}</div><div className="val" style={{ fontSize: 18 }}>{summaryValue(k, v)}</div></div>
              ))}
            </div>
          )}
          {blocks.map((b, i) => (
            <div key={i} className="card" style={{ marginTop: i === 0 ? 0 : 18 }}>
              {b.title && <h2>{b.title}</h2>}
              <Table columns={b.columns} rows={b.rows} />
            </div>
          ))}
          {r.notes?.map((n, i) => <div key={i} className="hint" style={{ marginTop: 10 }}>{n}</div>)}
          <div className="hint print:hidden" style={{ marginTop: 6 }}>Period {formatDate(r.from)} → {formatDate(r.to)} · generated {new Date().toLocaleString('en-IN')}</div>
        </>
      )}
    </div>
  )
}
